import React from "react";
import { useMutation, FunctionField, useTranslate } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import CircularProgress from '@material-ui/core/CircularProgress';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { toPrice } from '../../../components/fields/PriceField';

import { currencyOptions, currencyLocales } from '../../../components/fields/PriceField';

// TODO: as an improvement we can pass local to Intl.Collator from default owner company settings instead of “undefined“
const COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
const GRID_TEMPLATE_COLUMNS = 'minmax(220px, 1fr) 110px 110px 70px';

const useStyles = makeStyles({
	nowrap: {
		whiteSpace: 'nowrap',
	},
	input: {
		width: '100%',
	},
	gridRow: {
		display: 'grid',
		gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
		columnGap: '12px',
		alignItems: 'center',
		padding: '8px 16px',
	},
	header: {
		borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
		paddingTop: '10px',
		paddingBottom: '10px',
		position: 'sticky',
		top: 0,
		zIndex: 1,
		backgroundColor: '#fff',
	},
	body: {
		padding: '8px 0',
	},
	headerText: {
		fontWeight: 600,
	},
	numberCell: {
		textAlign: 'left',
	},
	popoverContent: {
		minWidth: '480px',
		maxHeight: '520px',
		overflowY: 'auto',
	},
	emptyValue: {
		opacity: 0.5,
	},
	loading: {
		padding: '24px',
		display: 'flex',
		justifyContent: 'center',
	},
	empty: {
		padding: '16px',
	},
});

const SPECIAL_PRICE_FIELDS = ["price", "min_price"];

const getSpecialPriceValue = (draft, item, field) => {
	return draft?.companies?.[item.company_id]?.[field] != null
		? draft.companies[item.company_id][field]
		: item[field];
};

const buildSpecialPriceDraft = ({ currentDraft, articleCode, terminalId, currency, companyId, field, value }) => ({
	...currentDraft,
	article_code: articleCode,
	terminal_id: terminalId,
	currency,
	companies: {
		...(currentDraft?.companies || {}),
		[companyId]: {
			...(currentDraft?.companies?.[companyId] || {}),
			[field]: value,
		},
	},
});

const hasSpecialPriceDraftChanges = (selectedFields, recordId, companyId, field) =>
	Object.prototype.hasOwnProperty.call(
		selectedFields[recordId]?.special_price?.companies?.[companyId] || {},
		field
	);


const useSortedByCompany = (data) => {
	return React.useMemo(() => {
		if (!data?.length) {
			return [];
		}

		return [...data].sort((a, b) => {
			const nameA = a.company_name || '';
			const nameB = b.company_name || '';

			const idA = a.company_id || 0;
			const idB = b.company_id || 0;

			const byName = COLLATOR.compare(nameA, nameB);
			if (byName !== 0) {
				return byName;
			} else {
				return idA - idB;
			}    
			});
		}, [data]);
};


const SpecialPricesButton = (props) => {
	const classes = useStyles();
	const translate = useTranslate()
	const [anchorEl, setAnchorEl] = React.useState(null);
	const [prices, setPrices] = React.useState({});

	React.useEffect(() => {
		if (!props.selectedFields[props.recordId]) {
			setPrices({});
		}
	}, [props.selectedFields]);

	const [approve, { loading, data }] = useMutation({
		type: 'getManyReference',
		resource: 'pricelist-special-price',
		payload: {
			filter: {
				article_code: props.articleCode,
				terminal_id: props.terminalId,
				currency: props.selectedCurrency
			},
			pagination: { page: 1, perPage: 10 },
			sort: { field: 'id', order: 'DESC' },
		}
	});

	const sortedSpecialPrices = useSortedByCompany(data);

	const handleClick = (e) => {
		approve();
		setAnchorEl(e.currentTarget);
		e.preventDefault();
		e.stopPropagation();
	};

	const handleClose = (e) => {
		setAnchorEl(null);
		e.preventDefault();
		e.stopPropagation();
	};

	const handlePriceChange = (e, item, recordId, articleCode) => {
		const field = e.target.name || 'price';
		const newPrice = e.target.value;
		if (newPrice !== '' && Number(newPrice) < 0) {
			return;
		}
		const updatedRecord = buildSpecialPriceDraft({
			currentDraft: prices[recordId],
			articleCode,
			terminalId: item.terminal_id,
			currency: props.selectedCurrency,
			companyId: item.company_id,
			field,
			value: newPrice === "" ? "" : newPrice,
		});
		const updatedPrices = {
			...prices,
			[recordId]: updatedRecord,
		};

		setPrices(updatedPrices);
		props.updateSelectedFieldsSpecialPrice(recordId, updatedRecord);
		props.updateSelectedIds(recordId);
	};

	const open = Boolean(anchorEl);
	const id = open ? 'simple-popover' : undefined;

	const getSpecialPriceColor = (item, field) => {
		if (hasSpecialPriceDraftChanges(props.selectedFields, props.recordId, item.company_id, field)) {
			return 'orange';
		}
		return undefined;
	}

	const getSpecialPriceButtonColor = (data) => {
		if (data && data.length && props.selectedFields[props.recordId]) {
			for (const item of data) {
				if (SPECIAL_PRICE_FIELDS.some((field) => hasSpecialPriceDraftChanges(props.selectedFields, props.recordId, item.company_id, field))) {
					return 'orange';
				}
			}
		}
		return undefined;
	}

	return (
		<div>
			<Button size="small" aria-describedby={id} variant="outlined" color="primary" onClick={handleClick} 
				style={{ backgroundColor: getSpecialPriceButtonColor(data) }}
			>
				{translate("resources.pricelist-articles.button.prices")}
			</Button>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'center',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'center',
					horizontal: 'right',
				}}
			>
				<Box className={classes.popoverContent}>
					<Box className={`${classes.gridRow} ${classes.header}`}>
						<Typography variant="caption" className={classes.headerText}>
							{translate("resources.pricelist-articles.fields.name")}
						</Typography>
						<Typography variant="caption" className={classes.headerText}>
							{translate("resources.pricelist-articles.fields.price")}
						</Typography>
						<Typography variant="caption" className={classes.headerText}>
							{translate("resources.pricelist-articles.fields.min_price")}
						</Typography>
						<Typography variant="caption" className={classes.headerText}>
							{translate("resources.pricelist-articles.fields.currency")}
						</Typography>
					</Box>
					{loading ? (
						<Box className={classes.loading}>
							<CircularProgress />
						</Box>
					) : sortedSpecialPrices.length ? (
						<Box className={classes.body}>
							{sortedSpecialPrices.map(item => (
								<Box key={item.company_id} className={`${classes.nowrap} ${classes.gridRow}`}>
									<Box>
										<Typography variant="body2">{item.company_name}</Typography>
									</Box>
									{!props.editable ? (
										<>
											<Box className={classes.numberCell}>
												<Typography variant="body2">
													{toPrice(item.price, props.selectedCurrency, true)}
												</Typography>
											</Box>
											<Box className={classes.numberCell}>
												<Typography variant="body2">
													{toPrice(item.min_price, props.selectedCurrency, true)}
												</Typography>
											</Box>
										</>
									) : (
										<>
											{SPECIAL_PRICE_FIELDS.map((field) => (
												<TextField
													key={field}
													className={classes.input}
													name={field}
													value={getSpecialPriceValue(prices[props.recordId], item, field)}
													onChange={(e) => handlePriceChange(e, item, props.recordId, props.articleCode)}
													variant="outlined"
													size="small"
													type="number"
													inputProps={{
														min: 0,
														step: 1,
														pattern: "[0-9]*",
														style: { padding: '8px', backgroundColor: getSpecialPriceColor(item, field) },
													}}
												/>
											))}
										</>
									)}
									<Box className={classes.numberCell}>
										<Typography variant="body2">{props.selectedCurrency}</Typography>
									</Box>
								</Box>
							))}
						</Box>
					) : (
						<Box className={classes.empty}>
							<Typography variant="body2">{translate("resources.pricelist-articles.no_special_prices")}</Typography>
						</Box>
					)}
				</Box>
			</Popover>
		</div>
	);
}


export const SpecialPricesField = (props) => {
    return (
        <FunctionField
            {...props}
            render={() => (
                <SpecialPricesButton
                    recordId={props.record.id}
                    updateSelectedIds={props.updateSelectedIds}
                    updateSelectedFieldsSpecialPrice={props.updateSelectedFieldsSpecialPrice} selectedCurrency={props.selectedCurrency}
                    selectedIds={props.selectedIds}
                    selectedFields={props.selectedFields}
                    editable={props.editable}
                    articleCode={props.record.code}
                    terminalId={props.record.terminal_id}
                />
            )}
        />
    );
};

SpecialPricesField.defaultProps = {
	addLabel: true,
};
